import { Text, useBreakpoint } from "@chakra-ui/react";
import dayjs from "dayjs";
import type {
  APIListingAgentUpdateEntry,
  APIListingApplicantFeedbackEntry,
  APIListingEventFeedbackEntry,
} from "models/types/api-listing";
import { Link } from "react-router-dom";
import { CardRecordMeta } from "components/cards/card-record-meta";
import { useFormatMoney } from "hooks/use-format-money";
import { EventOrFeedbackTypeTag } from "modules/vendor-portal/components/event-or-feedback-type-tag";
import { InterestLevelBadge } from "modules/vendor-portal/components/interest-level-badge";
import { FooterText } from "modules/vendor-portal/screens/overview/components/feedback/footer/footer-text";
import { dateFormats } from "utils/date";
import { numberToOrdinal } from "utils/format-ordinal-number";

type OpenHomeFooterProps = {
  feedback:
    | APIListingApplicantFeedbackEntry
    | APIListingAgentUpdateEntry
    | APIListingEventFeedbackEntry;
};

export function OpenHomeFooter({ feedback }: OpenHomeFooterProps) {
  const formatMoney = useFormatMoney();

  const breakpoint = useBreakpoint({ ssr: false });
  const isMobile = breakpoint === "base" || breakpoint === "sm";

  return (
    <CardRecordMeta>
      <EventOrFeedbackTypeTag type={feedback.type} />

      {"interest_level" in feedback && feedback.interest_level ? (
        <InterestLevelBadge interestLevel={feedback.interest_level} />
      ) : null}

      {isMobile ||
      !("price_indication" in feedback) ||
      !feedback.price_indication ? null : (
        <FooterText
          text={`Price indication ${formatMoney(feedback.price_indication)}`}
        />
      )}

      {"attendees" in feedback &&
      feedback.attendees &&
      feedback.attendees.length === 0
        ? null
        : "attendees" in feedback &&
          feedback.attendees &&
          feedback.attendees[0] && (
            <FooterText
              text={`${numberToOrdinal(
                Math.max(
                  ...feedback.attendees.map(
                    (a) => a.times_inspected_running_total,
                  ),
                ),
              )} inspection`}
            />
          )}

      {"date_of" in feedback && (
        <FooterText
          text={dayjs(feedback.date_of).format(dateFormats.dayMonthYear)}
        />
      )}

      {isMobile ? null : <FooterText text={`Agent ${feedback.agent.name}`} />}

      {"event" in feedback && feedback.event ? (
        <Link to={`../events/${feedback.event.id}`}>
          <Text
            color={"blue.600"}
            fontSize={"md"}
            fontWeight={500}
            lineHeight={"20px"}
          >
            View event details
          </Text>
        </Link>
      ) : null}
    </CardRecordMeta>
  );
}
